import { computed, inject, ref, watch, type Ref } from 'vue'
import { modalStackEntryZKey, modalStackKey } from '../lib/modal-stack'

export const globalZIndex = ref(50)

export const dialogsOpen = ref(0)

/** A composable to help determine the z-index value of dialogs when multiple are shown at the same time. */
export function useDialogZIndex(isOpen?: Ref<boolean>) {
  // If this dialog is rendered as a stack entry, the central host injects its
  // z; take it and skip the global counter entirely. The distributed counter
  // (incremented inside each modal's own watcher) is the refresh-ordering race
  // the central-modal-host plan removes — a stacked modal's z is a pure
  // projection of its stack index instead.
  const stackEntryZ = inject(modalStackEntryZKey, null)
  // For transient overlays (confirms/popovers/menus opened from within a
  // stacked modal — NOT stack entries themselves), float above the current
  // stack top so they're never hidden behind it.
  const stack = inject(modalStackKey, null)

  const localZIndex = ref(50)

  if (!stackEntryZ && isOpen) {
    watch(
      () => isOpen.value,
      (val) => {
        if (val) {
          // Assign a strictly increasing z so a dialog opened later always
          // stacks above one opened earlier — even when opens/closes don't
          // nest LIFO (e.g. the combobox that spawned a dialog closing while
          // that dialog stays open). Decrementing globalZIndex on close (the
          // old behaviour) let a later dialog reuse an earlier one's z and tie,
          // rendering behind it. `floor` keeps a transient above the central
          // stack top whenever one is mounted.
          const floor = stack ? stack.topZ.value : 0
          globalZIndex.value = Math.max(globalZIndex.value + 1, floor + 1)
          localZIndex.value = globalZIndex.value
          dialogsOpen.value += 1
        } else {
          localZIndex.value = 50
          dialogsOpen.value = Math.max(0, dialogsOpen.value - 1)
          // Reset the allocator only once nothing is stacked, so it stays
          // monotonic while dialogs overlap yet never grows unbounded across a
          // long session.
          if (dialogsOpen.value === 0) {
            globalZIndex.value = 50
          }
        }
      }
    )
  }

  const showOverlay = computed(() => dialogsOpen.value <= 1)

  const zIndex: Readonly<Ref<number>> = stackEntryZ ?? localZIndex

  return {
    zIndex,
    globalZIndex,
    showOverlay
  }
}
